// AppStatusPill
// -------------
// Status chip that summarizes a webapp's current state in one of four
// mutually-exclusive shapes:
//
//   Not Verified  — amber, ownership verification still pending
//   Testing       — blue,  pulsing dot while a test is in flight
//   Not Tested    — gray,  verified but no completed test yet
//   <Result>      — tone-colored, label sourced from the shared
//                   getTestResultLabel helper (Completed / Test
//                   Failed / Live — the "Live" tone never reaches
//                   this branch because the Testing state takes
//                   precedence)
//
// Was inlined verbatim in Dashboard.tsx (ApplicationCard) and
// Applications.tsx (row). Centralised so a future state addition
// (e.g. "Awaiting fix") or copy tweak only needs to land in one place.

import React from "react";
import { TestResultLabel } from "@/utils/testStatusLabel";
import TestResultPill from "@/components/common/TestResultPill";

interface AppStatusPillProps {
  isVerified: boolean;
  hasTested: boolean;
  isRunning: boolean;
  resultLabel: TestResultLabel;
  // Extra utility classes — e.g. `shrink-0` on the Applications row,
  // where the pill sits beside a truncating app name. Optional; the
  // Dashboard card placement doesn't need it.
  className?: string;
}

const BASE = "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium";

export default function AppStatusPill({
  isVerified,
  hasTested,
  isRunning,
  resultLabel,
  className = "",
}: AppStatusPillProps) {
  const trailing = className ? ` ${className}` : "";

  if (!isVerified) {
    return (
      <span className={`${BASE} bg-amber-50 text-amber-700${trailing}`}>
        <span className="inline-flex h-1.5 w-1.5 rounded-full bg-amber-400" />
        Not Verified
      </span>
    );
  }

  // Testing takes precedence over Not Tested: an in-flight run — including
  // an app's first-ever test, where hasTested is still false — reads
  // "Testing", never "Not Tested". Only the (rare) unverified case above
  // outranks it, since a test can't run on an unverified app.
  if (isRunning) {
    return (
      <span className={`${BASE} bg-blue-50 text-blue-700${trailing}`}>
        <span className="relative flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-blue-500" />
        </span>
        Testing
      </span>
    );
  }

  if (!hasTested) {
    return (
      <span className={`${BASE} bg-gray-100 text-gray-500${trailing}`}>
        <span className="inline-flex h-1.5 w-1.5 rounded-full bg-gray-400" />
        Not Tested
      </span>
    );
  }

  // Result branch — defers to the shared TestResultPill so the
  // pill's chrome (font, dot, padding) stays in lockstep with every
  // other place that surfaces a test result.
  return <TestResultPill resultLabel={resultLabel} className={className} />;
}
